import { FC, useEffect, useMemo, useRef } from 'react';
import { Grid, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import { useStyles } from './styles';
import ChatMessage from './parts/ChatMessage';

interface ChatProps {
    mobileLayout?: boolean;
    enabled?: boolean;
}

const Chat: FC<ChatProps> = ({ mobileLayout, enabled }) => {
    const classes = useStyles();
    const chatRef = useRef(null);
    const scrollBottomRef = useRef(null);
    const { t } = useTranslation();
    const {
        user: { user_ide: userIde, user_username: userUsername },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const {
        eventData: {
            user: { user_ide: influencerUserIde },
        },
        streamingState: {
            // @ts-ignore
            current_state: { banned_viewers: bannedViewers, admin_users: adminUsers },
        },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const isAdmin = useMemo(() => {
        return adminUsers?.find((username: string) => username === userUsername);
    }, [adminUsers, userUsername]);

    const {
        streamingChat,
        streamingState: { current_state: currentState },
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    useEffect(() => {
        if (scrollBottomRef.current) {
            // @ts-ignore
            scrollBottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [streamingChat]);

    const renderChatInput = () => {
        return currentState?.chat_enabled;
    };

    if (!renderChatInput() && enabled) {
        return (
            <Grid
                container
                xs={12}
                justify="center"
                alignContent="center"
                alignItems="center"
                direction="row"
            >
                <Grid
                    container
                    item
                    xs={12}
                    direction="row"
                    className={clsx(classes.container, mobileLayout ? '' : classes.desktopContainer)}
                >
                    <Grid
                        xs={12}
                        container
                        justify="flex-start"
                        alignContent="center"
                        alignItems="center"
                        direction="row"
                    >
                        <Grid ref={chatRef} item xs={mobileLayout ? 10 : 12}>
                            <Typography
                                className={clsx(
                                    classes.chatDisabledMessage,
                                    mobileLayout ? '' : classes.desktopDisabledMessage,
                                )}
                            >
                                {t('Chat disabled')}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container xs={12} justify="center" alignContent="center" alignItems="center" direction="row">
            <Grid
                container
                item
                xs={12}
                direction="row"
                className={clsx(
                    classes.container,
                    mobileLayout ? classes.absolutePosition : classes.desktopContainer,
                )}
            >
                <Grid
                    xs={12}
                    container
                    justify="flex-start"
                    alignContent="flex-start"
                    alignItems="flex-start"
                    direction="row"
                >
                    <Grid ref={chatRef} item xs={mobileLayout ? 10 : 12}>
                        {streamingChat.map((message, index) => {
                            return (
                                <ChatMessage
                                    key={index}
                                    mobileLayout={mobileLayout}
                                    avatar={message.user.user_avatar}
                                    message={message.message}
                                    username={message.user.user_username}
                                    isInfluencer={message.user.user_role === 1}
                                    hasAdminPermissions={isEventInfluencer || isAdmin}
                                    isEventOwner={message.user.user_ide === influencerUserIde}
                                    isBanned={
                                        !!bannedViewers.find(
                                            (username: string) => username === message.user.user_username,
                                        )
                                    }
                                />
                            );
                        })}
                        <div ref={scrollBottomRef}> </div>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Chat;
